import React, { createContext, useContext, useState, useEffect, useRef, ReactNode } from 'react';
import { useAuth } from './AuthContext';
import { authFetch } from '@/lib/auth';

interface ActivityTrackerContextType {
    totalMinutes: number;
    isTracking: boolean;
}

const ActivityTrackerContext = createContext<ActivityTrackerContextType | undefined>(undefined);

export const useActivityTracker = () => {
    const context = useContext(ActivityTrackerContext);
    if (!context) {
        throw new Error('useActivityTracker must be used within ActivityTrackerProvider');
    }
    return context;
};

interface ActivityTrackerProviderProps {
    children: ReactNode;
}

export const ActivityTrackerProvider: React.FC<ActivityTrackerProviderProps> = ({ children }) => {
    const { session, user } = useAuth();
    const [totalMinutes, setTotalMinutes] = useState(0);
    const [isTracking, setIsTracking] = useState(true);

    const startTimeRef = useRef<number>(Date.now());
    const accumulatedSecondsRef = useRef<number>(0);
    const lastSyncRef = useRef<number>(Date.now());
    const intervalRef = useRef<NodeJS.Timeout | null>(null);

    // Sync accumulated time to backend
    const syncToBackend = async (minutes: number) => {
        if (!session || !user || minutes === 0) return;

        try {
            await authFetch('/api/user/stats/update', {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                },
                body: JSON.stringify({
                    // Backend validation expects these fields to exist.
                    // This activity tracker only knows about study time, so other values default to 0.
                    study_minutes: Math.floor(minutes),
                    flashcards_studied: 0,
                    mastery_improvement: 0
                })
            });

        } catch (error) {
            console.error('Failed to sync study time:', error);
        }
    };

    // Handle visibility change (tab switching, minimizing)
    useEffect(() => {
        const handleVisibilityChange = () => {
            if (document.hidden) {
                // User left the app - pause tracking
                setIsTracking(false);

                // Calculate time since last sync
                const now = Date.now();
                const secondsSinceLastSync = (now - lastSyncRef.current) / 1000;
                accumulatedSecondsRef.current += secondsSinceLastSync;

                // Sync to backend if we have at least 1 minute
                const minutesToSync = accumulatedSecondsRef.current / 60;
                if (minutesToSync >= 1) {
                    syncToBackend(minutesToSync);
                    accumulatedSecondsRef.current = 0;
                    lastSyncRef.current = now;
                }
            } else {
                // User returned - resume tracking
                setIsTracking(true);
                lastSyncRef.current = Date.now();
            }
        };

        document.addEventListener('visibilitychange', handleVisibilityChange);
        return () => document.removeEventListener('visibilitychange', handleVisibilityChange);
        // Boolean(session), not session — see Dashboard.tsx for why.
    }, [Boolean(session), user]);

    // Main tracking interval
    useEffect(() => {
        if (!session || !user) {
            setIsTracking(false);
            return;
        }

        setIsTracking(true);
        lastSyncRef.current = Date.now();

        // Update every second
        intervalRef.current = setInterval(() => {
            if (isTracking && !document.hidden) {
                const now = Date.now();
                const secondsSinceLastSync = (now - lastSyncRef.current) / 1000;
                accumulatedSecondsRef.current += secondsSinceLastSync;
                lastSyncRef.current = now;

                // Update total minutes for display
                setTotalMinutes(Math.floor(accumulatedSecondsRef.current / 60));

                // Sync to backend every 5 minutes
                if (accumulatedSecondsRef.current >= 300) { // 5 minutes
                    const minutesToSync = accumulatedSecondsRef.current / 60;
                    syncToBackend(minutesToSync);
                    accumulatedSecondsRef.current = 0;
                }
            }
        }, 1000);

        return () => {
            if (intervalRef.current) {
                clearInterval(intervalRef.current);
            }
        };
        // Boolean(session), not session — Supabase re-emits a new session
        // object roughly every second even when nothing meaningful changed.
        // Depending on the object itself re-ran this effect that often,
        // which reset lastSyncRef.current = Date.now() on every re-run — so
        // secondsSinceLastSync inside the interval tick was always ~0 and
        // accumulatedSecondsRef never actually accumulated. Study time
        // tracking (the 5-minute backend sync, and the totalMinutes shown to
        // the user) was silently broken for as long as this ran. Same bug
        // class found and fixed across Dashboard.tsx / SummariesPage.tsx /
        // MasteryDashboard.tsx / AnalyticsDashboard.tsx.
    }, [Boolean(session), user, isTracking]);

    // Sync on unmount (user closes app)
    useEffect(() => {
        return () => {
            const minutesToSync = accumulatedSecondsRef.current / 60;
            if (minutesToSync >= 0.5) { // Sync if at least 30 seconds
                syncToBackend(minutesToSync);
            }
        };
    }, []);

    return (
        <ActivityTrackerContext.Provider value={{ totalMinutes, isTracking }}>
            {children}
        </ActivityTrackerContext.Provider>
    );
};
